import Tooltip from '@mui/material/Tooltip';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import checkSvg from '../../../assets/img/check-circle.svg';
import { getUserType } from '../../../redux/slices/userSlice';
import { ICL, TaskKeysType, TestItem, ExcludeTask, WorkType } from '../../../shared/types';
import { ExcludeAnswer, SelectOneAnswer } from '../../../shared/types/task';
import StudentTaskWrapper from '../StudentTaskWrapper';

interface StudentExcludeTaskProps extends ExcludeTask {
  icl: ICL | null;
  handleChangeTaskByKey: (
    id: string,
    key: TaskKeysType,
    value: string[] | string | boolean | ExcludeAnswer[] | TestItem[] | SelectOneAnswer[],
  ) => void;
  saveTaskAnswer: (id: string) => Promise<void>;
  hw?: boolean;
  preview?: boolean;
  studentName?: string;
  checkedIds?: string[];
  index: number;
  points: number;
  workTaskType?: WorkType;
  workType?: WorkerType;
}

export default function StudentExcludesTask({
  icl,
  text,
  answerOptions,
  hasAnswer,
  hasStar,
  checkedAt,
  completedAt,
  handleChangeTaskByKey,
  id,
  hasPhoto,
  answersPhotos,
  saveTaskAnswer,
  deadline,
  audioUrl,
  videoUrl,
  taskPhotos,
  editedAnswerPhotos,
  hw,
  pdfUrl,
  wordUrl,
  studentPdfUrl,
  studentWordUrl,
  preview,
  options,
  answer,
  index,
  studentName,
  checkedIds,
  workTy,
  points,
  workType,
  workTaskType,
  teacherPoints,
}: StudentExcludeTaskProps) {
  const userType = useSelector(getUserType);
  const { t } = useTranslation();

  const handleSetAnswer = (word: string, lineId: string) => {
    if (!answerOptions) {
      handleChangeTaskByKey(id, 'answerOptions', [{ lineId, words: [word] }]);
    } else {
      const targetLine = answerOptions.find(it => it.lineId === lineId);

      if (!targetLine) {
        handleChangeTaskByKey(id, 'answerOptions', [...answerOptions, { lineId, words: [word] }]);
      } else {
        const newAnswerOptions = answerOptions.map(it => {
          if (it.lineId === lineId) {
            if (it.words.includes(word)) {
              return { ...it, words: it.words.filter(it => it !== word) };
            } else {
              return { ...it, words: [...it.words, word] };
            }
          }

          return it;
        });

        handleChangeTaskByKey(id, 'answerOptions', newAnswerOptions);
      }
    }
  };

  const renderTeacherChoices = (word: string, excludeWords: string[]) => {
    if (userType !== 'Student' && excludeWords.includes(word)) {
      return (
        <Tooltip
          title={t('wordChoiseTeacher')}
          componentsProps={{
            popper: {
              sx: {
                ['& .MuiTooltip-arrow']: {
                  color: '#9CDFFC',
                },
                ['& .MuiTooltip-tooltip']: {
                  backgroundColor: '#9CDFFC',
                  color: '#000000',
                  fontSize: '14px',
                  padding: '14px',
                  borderRadius: '8px',
                },
              },
            },
          }}
          arrow
        >
          <img src={checkSvg} alt="check" />
        </Tooltip>
      );
    }
  };

  const renderOptions = (words: string[], excludeWords: string[], id: string) => {
    return words.map((word, index) => {
      const isAnswer = answerOptions?.find(it => it.lineId === id && it.words.includes(word));

      return (
        <div
          key={index}
          className={`flex grow cursor-pointer items-center justify-center gap-[15px] overflow-hidden rounded-[8px] bg-[#DCF0FF] p-[12px] ${isAnswer && 'del-cross border-[2px] border-[#52C7FA]'}`}
          onClick={() => {
            if (userType === 'Student' && !checkedIds?.includes(id)) {
              handleSetAnswer(word, id);
            }
          }}
        >
          {renderTeacherChoices(word, excludeWords)}

          {word}
        </div>
      );
    });
  };

  const renderOptionsLines = () => {
    if (options.length > 0) {
      return options?.map((option, index) => {
        return (
          <div key={option.id} className="mb-[6px] mt-[10px]">
            <div>{index + 1}.</div>

            <div className="mr-[10px] mt-[12px] flex flex-wrap gap-[10px]">
              {renderOptions(option.words, option.excludeWords, option.id)}
            </div>
          </div>
        );
      });
    }
  };

  const isDisabledSubmitButton = useMemo(
    () => !answerOptions || !!checkedAt || !!completedAt,
    [answerOptions, checkedAt, completedAt],
  );

  return (
    <StudentTaskWrapper
      icl={icl}
      taskText={text}
      answersPhotos={answersPhotos}
      id={id}
      handleChangeTaskByKey={handleChangeTaskByKey}
      checkedAt={checkedAt}
      hasPhoto={hasPhoto}
      completedAt={completedAt}
      saveTaskAnswer={saveTaskAnswer}
      deadline={deadline}
      audioUrl={audioUrl}
      hasAnswer={hasAnswer}
      hasStar={hasStar}
      videoUrl={videoUrl}
      taskPhotos={taskPhotos}
      editedAnswerPhotos={editedAnswerPhotos}
      hw={hw}
      pdfUrl={pdfUrl}
      wordUrl={wordUrl}
      studentPdfUrl={studentPdfUrl}
      studentWordUrl={studentWordUrl}
      preview={preview}
      isDisabledSubmitButton={isDisabledSubmitButton}
      isSubmitButton
      answer={answer}
      taskName={'excludesTask'}
      studentName={studentName}
      index={index}
      points={points}
      workType={workType}
      workTaskType={workTaskType}
      teacherPoints={teacherPoints}
    >
      <>
        <div>{renderOptionsLines()}</div>
      </>
    </StudentTaskWrapper>
  );
}
